import { useSyncExternalStore } from 'react';

export type LivePollInterval<T> =
  | number
  | null
  | ((snapshot: LiveResourceSnapshot<T>) => number | null);

export type LiveFetchReason =
  | 'subscribe'
  | 'poll'
  | 'manual'
  | 'bootstrap'
  | (string & {});

export interface LiveResourceSnapshot<T> {
  data: T | null;
  loading: boolean;
  hasValue: boolean;
  error: string | null;
  version: number;
  lastSuccessAt: number | null;
  lastAttemptAt: number | null;
  lastReason: string | null;
}

export interface LiveResourceDebugState {
  id: string;
  listeners: number;
  polling: boolean;
  pollIntervalMs: number | null;
  inFlight: boolean;
  refreshCount: number;
  errorCount: number;
  attemptsByReason: Record<string, number>;
  changesBySource: Record<string, number>;
  lastSource: string | null;
  lastReason: string | null;
  lastSuccessAt: number | null;
  lastAttemptAt: number | null;
}

export interface LiveResourceOptions<T> {
  id: string;
  fetch: (reason: LiveFetchReason) => Promise<T>;
  pollIntervalMs?: LivePollInterval<T>;
  isEqual?: (a: T, b: T) => boolean;
  resolveSource?: (
    nextData: T,
    previousData: T | null,
    reason: LiveFetchReason
  ) => string | null;
  refreshOnSubscribe?: boolean;
}

export interface LiveResource<T> {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => LiveResourceSnapshot<T>;
  refresh: (reason?: LiveFetchReason) => Promise<LiveResourceSnapshot<T>>;
  /**
   * Crosses an authorization/resource boundary: clear cached data immediately,
   * detach any older in-flight request, and prevent that request from
   * repopulating the resource when it eventually completes.
   */
  invalidate: (reason?: string) => void;
  updateLocal: (updater: (current: T | null) => T | null, reason?: string) => void;
  getDebugState: () => LiveResourceDebugState;
  dispose: () => void;
}

const debugRegistry = new Map<string, () => LiveResourceDebugState>();
let debugHookInitialized = false;

function incrementCounter(counter: Record<string, number>, key: string) {
  counter[key] = (counter[key] ?? 0) + 1;
}

function ensureWindowDebugHook() {
  if (debugHookInitialized || typeof window === 'undefined') {
    return;
  }
  debugHookInitialized = true;
  const target = window as Window & {
    __codexLiveResources?: {
      debug: () => LiveResourceDebugState[];
    };
  };
  target.__codexLiveResources = {
    debug: () => getAllLiveResourceDebugStates()
  };
}

export function getAllLiveResourceDebugStates(): LiveResourceDebugState[] {
  return [...debugRegistry.values()].map((resolver) => resolver());
}

export function createLiveResource<T>(options: LiveResourceOptions<T>): LiveResource<T> {
  ensureWindowDebugHook();

  let snapshot: LiveResourceSnapshot<T> = {
    data: null,
    loading: false,
    hasValue: false,
    error: null,
    version: 0,
    lastSuccessAt: null,
    lastAttemptAt: null,
    lastReason: null
  };

  let disposed = false;
  let refreshCount = 0;
  let errorCount = 0;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let pollIntervalMs: number | null = null;
  let inFlight: Promise<LiveResourceSnapshot<T>> | null = null;
  let requestGeneration = 0;
  let lastSource: string | null = null;
  const listeners = new Set<() => void>();
  const attemptsByReason: Record<string, number> = {};
  const changesBySource: Record<string, number> = {};
  const equals = options.isEqual ?? ((a, b) => Object.is(a, b));
  const refreshOnSubscribe = options.refreshOnSubscribe ?? true;

  const notify = () => {
    for (const listener of listeners) {
      listener();
    }
  };

  const clearPollTimer = () => {
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
  };

  const resolvePollInterval = () => {
    const pollConfig = options.pollIntervalMs ?? null;
    if (typeof pollConfig === 'function') {
      return pollConfig(snapshot);
    }
    return pollConfig;
  };

  const schedulePolling = () => {
    clearPollTimer();
    if (disposed || listeners.size === 0) {
      pollIntervalMs = null;
      return;
    }
    const nextInterval = resolvePollInterval();
    pollIntervalMs = nextInterval;
    if (!nextInterval || nextInterval <= 0) {
      return;
    }
    pollTimer = setTimeout(() => {
      void refresh('poll');
    }, nextInterval);
  };

  const refresh = async (reason: LiveFetchReason = 'manual') => {
    if (disposed) {
      return snapshot;
    }
    if (inFlight) {
      return inFlight;
    }
    incrementCounter(attemptsByReason, reason);
    const generation = requestGeneration;

    const prev = snapshot;
    snapshot = {
      ...prev,
      loading: true,
      error: null,
      lastAttemptAt: Date.now(),
      lastReason: reason
    };
    notify();

    let request: Promise<LiveResourceSnapshot<T>> | null = null;
    request = (async () => {
      try {
        const nextData = await options.fetch(reason);
        if (generation !== requestGeneration || disposed) {
          return snapshot;
        }
        const now = Date.now();
        const changed = !prev.hasValue || !equals(prev.data as T, nextData);
        if (changed) {
          const source =
            options.resolveSource?.(nextData, prev.data, reason) ?? reason;
          lastSource = source;
          incrementCounter(changesBySource, source);
        }
        snapshot = {
          data: nextData,
          loading: false,
          hasValue: true,
          error: null,
          version: changed ? prev.version + 1 : prev.version,
          lastSuccessAt: now,
          lastAttemptAt: snapshot.lastAttemptAt,
          lastReason: reason
        };
        refreshCount += 1;
      } catch (err) {
        if (generation !== requestGeneration || disposed) {
          return snapshot;
        }
        const message = err instanceof Error ? err.message : String(err);
        snapshot = {
          ...snapshot,
          loading: false,
          error: message,
          lastReason: reason
        };
        errorCount += 1;
      } finally {
        if (inFlight === request) {
          inFlight = null;
        }
        if (generation === requestGeneration && !disposed) {
          notify();
          schedulePolling();
        }
      }
      return snapshot;
    })();
    inFlight = request;

    return inFlight;
  };

  const subscribe = (listener: () => void) => {
    if (disposed) {
      return () => undefined;
    }
    listeners.add(listener);
    if (listeners.size === 1) {
      if (refreshOnSubscribe) {
        void refresh(snapshot.hasValue ? 'subscribe' : 'bootstrap');
      }
      schedulePolling();
    }
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        clearPollTimer();
        pollIntervalMs = null;
      }
    };
  };

  const updateLocal = (updater: (current: T | null) => T | null, reason = 'local') => {
    if (disposed) {
      return;
    }
    const nextData = updater(snapshot.data);
    if (nextData === null) {
      snapshot = {
        ...snapshot,
        data: null,
        hasValue: false,
        version: snapshot.version + 1,
        lastReason: reason
      };
    } else {
      const changed = !snapshot.hasValue || !equals(snapshot.data as T, nextData);
      if (changed) {
        lastSource = reason;
        incrementCounter(changesBySource, reason);
      }
      snapshot = {
        ...snapshot,
        data: nextData,
        hasValue: true,
        version: changed ? snapshot.version + 1 : snapshot.version,
        lastSuccessAt: Date.now(),
        lastReason: reason,
        error: null
      };
    }
    notify();
    schedulePolling();
  };

  const invalidate = (reason = 'invalidate') => {
    if (disposed) {
      return;
    }
    requestGeneration += 1;
    // A refresh in the new generation must not be deduplicated against the
    // detached request. Its completion is ignored by the generation checks.
    inFlight = null;
    snapshot = {
      ...snapshot,
      data: null,
      loading: false,
      hasValue: false,
      error: null,
      version: snapshot.version + 1,
      lastReason: reason
    };
    lastSource = reason;
    incrementCounter(changesBySource, reason);
    notify();
    schedulePolling();
  };

  const getDebugState = () => ({
    id: options.id,
    listeners: listeners.size,
    polling: pollTimer !== null,
    pollIntervalMs,
    inFlight: inFlight !== null,
    refreshCount,
    errorCount,
    attemptsByReason: { ...attemptsByReason },
    changesBySource: { ...changesBySource },
    lastSource,
    lastReason: snapshot.lastReason,
    lastSuccessAt: snapshot.lastSuccessAt,
    lastAttemptAt: snapshot.lastAttemptAt
  });

  const dispose = () => {
    if (disposed) {
      return;
    }
    disposed = true;
    clearPollTimer();
    listeners.clear();
    debugRegistry.delete(options.id);
  };

  debugRegistry.set(options.id, getDebugState);

  return {
    subscribe,
    getSnapshot: () => snapshot,
    refresh,
    invalidate,
    updateLocal,
    getDebugState,
    dispose
  };
}

export function useLiveResource<T>(resource: LiveResource<T>) {
  return useSyncExternalStore(resource.subscribe, resource.getSnapshot, resource.getSnapshot);
}
